import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import SaveIcon from '@mui/icons-material/Save'
import DeleteIcon from '@mui/icons-material/Delete'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useApolloClient } from '@apollo/client'
import { getExploreRowById, getAllTags } from '../graphql/queries'
import { updateExploreRow, deleteExploreRow } from '../graphql/mutation'
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  Grid,
  Checkbox
} from '@mui/material'
import ExploreIcon from '@mui/icons-material/Explore'
import AppleIcon from '@mui/icons-material/Apple'
import AndroidIcon from '@mui/icons-material/Android'
import ProgressBar from '../components/circularProgress/circularProgress'

export default function ExploreRowPage() {
  const { id } = useParams()
  const client = useApolloClient()
  const navigate = useNavigate()

  const [exploreRow, setExploreRow] = useState({
    order: 1,
    isHighlighted: false,
    contentId: '',
    contentType: 'PROMPT',
    platform: ['IOS', 'ANDROID'],
    tagId: '',
    id: '',
    createdAt: '',
    updatedAt: ''
  })

  const [exploreRowData, setExploreRowData] = useState(null)
  const [progress, setProgress] = useState(false)
  const [deleteProgress, setDeleteProgress] = useState(false)
  const [snackBarOpen, setSnackBarOpen] = useState(false)
  const [snackBarMessage, setSnackBarMessage] = useState(null)
  const [snackBarSeverity, setSnackBarSeverity] = useState('success')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const { loading, error, data } = useQuery(getExploreRowById, {
    variables: { id: id },
    onCompleted: data => {
      setExploreRowData(data.getExploreRowById)
    },
  })

  const { data: tagsData } = useQuery(getAllTags)

  useEffect(() => {
    if (exploreRowData) {
      setExploreRow({
        order: exploreRowData.order,
        isHighlighted: exploreRowData.isHighlighted,
        contentId: exploreRowData.contentId,
        contentType: exploreRowData.contentType,
        platform: exploreRowData.platform || ['IOS', 'ANDROID'],
        tagId: exploreRowData.tagId || '',
        id: exploreRowData.id,
        createdAt: exploreRowData.createdAt,
        updatedAt: exploreRowData.updatedAt
      })
    }
  }, [exploreRowData])

  const [updateExploreRowMutation] = useMutation(updateExploreRow)
  const [deleteExploreRowMutation] = useMutation(deleteExploreRow)

  const handleSnackBarClose = () => {
    setSnackBarOpen(false)
  }

  const handleChange = (field, value) => {
    setExploreRow(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleUpdateExploreRow = async () => {
    if (!exploreRow.contentId.trim()) {
      setSnackBarMessage('Content ID is required')
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
      return
    }

    if (exploreRow.order < 1) {
      setSnackBarMessage('Order must be at least 1')
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
      return
    }

    setProgress(true)
    try {
      const variables = {
        id: exploreRow.id,
        order: exploreRow.order,
        isHighlighted: exploreRow.isHighlighted,
        contentId: exploreRow.contentId.trim(),
        contentType: exploreRow.contentType,
        platform: exploreRow.platform
      }

      // Only include tagId if it's not empty
      if (exploreRow.tagId) {
        variables.tagId = exploreRow.tagId
      }

      const response = await updateExploreRowMutation({
        variables
      })

      if (response.data) {
        setSnackBarMessage('Explore row updated successfully!')
        setSnackBarSeverity('success')
        setSnackBarOpen(true)
        
        // Update cache
        client.writeQuery({
          query: getExploreRowById,
          variables: { id: id },
          data: {
            getExploreRowById: {
              ...response.data.updateExploreRow,
              __typename: 'ExploreRow'
            }
          }
        })
      }
    } catch (error) {
      console.error('Error updating explore row:', error)
      setSnackBarMessage(`Error updating explore row: ${error.message}`)
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    } finally {
      setProgress(false)
    }
  }

  const handleDeleteExploreRow = async () => {
    setDeleteProgress(true)
    try {
      const response = await deleteExploreRowMutation({
        variables: {
          id: exploreRow.id
        }
      })

      if (response.data) {
        setSnackBarMessage('Explore row deleted successfully!')
        setSnackBarSeverity('success')
        setSnackBarOpen(true)
        
        // Navigate back to explore rows list after a short delay
        setTimeout(() => {
          navigate('/explore-rows')
        }, 1500)
      }
    } catch (error) {
      console.error('Error deleting explore row:', error)
      setSnackBarMessage(`Error deleting explore row: ${error.message}`)
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    } finally {
      setDeleteProgress(false)
      setDeleteDialogOpen(false)
    }
  }

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true)
  }

  if (loading) return <ProgressBar />
  if (error) return <Alert severity="error">Error loading explore row: {error.message}</Alert>
  if (!exploreRowData) return <Alert severity="error">Explore row not found</Alert>

  const getContentTypeColor = (type) => {
    switch (type) {
      case 'PROMPT': return 'primary'
      case 'STYLE': return 'secondary'
      case 'SECTION': return 'success'
      default: return 'default'
    }
  }

  const availableTags = tagsData?.getAllTags || []

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 4, 
      maxWidth: '800px', 
      mx: 'auto', 
      px: 2, 
      py: 2 
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <ExploreIcon fontSize="large" sx={{ color: '#1976d2' }} />
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
          Explore Row Details
        </Typography>
      </Box>

      {/* Main Content */}
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Grid container spacing={3}>
          {/* Basic Info */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Basic Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Order"
              type="number"
              value={exploreRow.order}
              onChange={(e) => handleChange('order', parseInt(e.target.value) || 1)}
              inputProps={{ min: 1 }}
              helperText="Display order in explore section"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={exploreRow.isHighlighted}
                    onChange={(e) => handleChange('isHighlighted', e.target.checked)}
                  />
                }
                label="Highlighted"
              />
              {exploreRow.isHighlighted && exploreRow.tagId && (
                <Alert severity="info" sx={{ mt: 1, fontSize: '0.875rem' }}>
                  <Typography variant="body2">
                    Only one content item per tag can be highlighted. Setting this as highlighted will automatically remove highlighting from other items in the same tag.
                  </Typography>
                </Alert>
              )}
            </Box>
          </Grid>

          {/* Content Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Content Information
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Content Type</InputLabel>
              <Select
                value={exploreRow.contentType}
                label="Content Type"
                onChange={(e) => handleChange('contentType', e.target.value)}
              >
                <MenuItem value="PROMPT">Prompt</MenuItem>
                <MenuItem value="STYLE">Style</MenuItem>
                <MenuItem value="SECTION">Section</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
              <Chip 
                label={exploreRow.contentType} 
                color={getContentTypeColor(exploreRow.contentType)}
                size="small"
                variant="outlined"
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Content ID"
              value={exploreRow.contentId}
              onChange={(e) => handleChange('contentId', e.target.value)}
              helperText="ID of the referenced content (Prompt, Style, or Section)"
            />
          </Grid>

          {/* Platform Selection */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Platform Selection
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={exploreRow.platform?.includes('IOS')}
                    onChange={(e) => {
                      const newPlatform = e.target.checked
                        ? [...(exploreRow.platform || []), 'IOS']
                        : (exploreRow.platform || []).filter(p => p !== 'IOS')
                      handleChange('platform', newPlatform)
                    }}
                    icon={<AppleIcon />}
                    checkedIcon={<AppleIcon />}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AppleIcon />
                    <Typography>iOS</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={exploreRow.platform?.includes('ANDROID')}
                    onChange={(e) => {
                      const newPlatform = e.target.checked
                        ? [...(exploreRow.platform || []), 'ANDROID']
                        : (exploreRow.platform || []).filter(p => p !== 'ANDROID')
                      handleChange('platform', newPlatform)
                    }}
                    icon={<AndroidIcon />}
                    checkedIcon={<AndroidIcon />}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AndroidIcon />
                    <Typography>Android</Typography>
                  </Box>
                }
              />
            </Box>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
              Select which platforms this explore row should be visible on
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              {exploreRow.platform?.includes('IOS') && (
                <Chip
                  icon={<AppleIcon />}
                  label="iOS"
                  size="small"
                  sx={{
                    backgroundColor: '#000',
                    color: '#fff',
                    '& .MuiChip-icon': { color: '#fff' }
                  }}
                />
              )}
              {exploreRow.platform?.includes('ANDROID') && (
                <Chip
                  icon={<AndroidIcon />}
                  label="Android"
                  size="small"
                  sx={{
                    backgroundColor: '#3DDC84',
                    color: '#000',
                    '& .MuiChip-icon': { color: '#000' }
                  }}
                />
              )}
              {!exploreRow.platform?.length && (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No platforms selected
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Tag Information */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Tag Information (Optional)
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Tag</InputLabel>
              <Select
                value={exploreRow.tagId}
                label="Tag"
                onChange={(e) => handleChange('tagId', e.target.value)}
              >
                <MenuItem value="">No Tag</MenuItem>
                {availableTags.map((tag) => (
                  <MenuItem key={tag.id} value={tag.id}>
                    {tag.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Tags help organize content. If highlighted, only one item per tag can be highlighted at a time.
            </Typography>
          </Grid>

          {/* Metadata */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              Metadata
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Row ID"
              value={exploreRow.id}
              disabled
              helperText="Unique identifier"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Created At"
              value={new Date(exploreRow.createdAt).toLocaleString()}
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Updated At"
              value={new Date(exploreRow.updatedAt).toLocaleString()}
              disabled
            />
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          mt: 4, 
          justifyContent: 'flex-end',
          flexWrap: 'wrap'
        }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
            disabled={deleteProgress}
          >
            Delete
          </Button>
          
          <Button
            variant="contained"
            startIcon={progress ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleUpdateExploreRow}
            disabled={progress}
            sx={{
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0'
              }
            }}
          >
            {progress ? 'Updating...' : 'Update Explore Row'}
          </Button>
        </Box>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Explore Row</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this explore row? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteExploreRow}
            color="error"
            disabled={deleteProgress}
            startIcon={deleteProgress ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleteProgress ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackBarOpen}
        autoHideDuration={6000}
        onClose={handleSnackBarClose}
      >
        <Alert onClose={handleSnackBarClose} severity={snackBarSeverity}>
          {snackBarMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}
